import { minutesToDurationInHours } from './filters';
import { StartWay } from './timerStart';
import t from '../i18n/t';

const padTwoDigits = (value: number) => (value < 10 ? `0${value}` : `${value}`);

export const secondsToStopwatchTime = (timeInSeconds: number) => {
  const total = Math.max(0, Math.floor(timeInSeconds));
  const hours = Math.floor(total / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  const seconds = total % 60;
  return { hours, minutes: padTwoDigits(minutes), seconds: padTwoDigits(seconds) };
};

export const calculateNewAvgTimePerPage = (timeSpent: number, pagesRead: number) => Math.floor((timeSpent * 60) / pagesRead);

export const secondsPassedFrom = (from: Date) => (new Date().getTime() - new Date(from).getTime()) / 1000;

export const getElapsedSeconds = (
  baseSeconds: number,
  startedInUtc: Date | string | null | undefined,
  isActive: boolean
) => {
  if (!isActive || !startedInUtc) {
    return Math.floor(baseSeconds || 0);
  }
  return Math.floor((baseSeconds || 0) + secondsPassedFrom(new Date(startedInUtc)));
};

/** Countdown shows the time left of the plan, Stopwatch the time used (timer spec, 2026-10-15). */
export type TimerMode = 'countdown' | 'stopwatch';

/**
 * The mode the student last chose, saved on their account; Stopwatch until they pick Countdown (Petr, 2026-10-17: what
 * students are used to). A choice already saved stays.
 */
export const savedTimerMode = (settings?: { timerMode?: string } | null): TimerMode =>
  settings?.timerMode === 'countdown' ? 'countdown' : 'stopwatch';

/** The ways a late start can go, as the "When I start a session late" setting lists them (bump.ts). */
export const LATE_START_WAYS: StartWay[] = ['bump', 'shortenThis', 'shortenNext', 'untilSession'];

/** The student's pick for a late start; bumping the day down unless they chose another (Petr, 2026-10-18). */
export const savedLateStart = (settings?: { lateStart?: string } | null): StartWay =>
  LATE_START_WAYS.find(way => way === settings?.lateStart) || 'bump';

/** The timer's clock: m:ss under an hour, h:mm:ss from an hour (59:58, 1:00:05). */
export const formatClock = (timeInSeconds: number) => {
  const { hours, minutes, seconds } = secondsToStopwatchTime(timeInSeconds);
  return hours ? `${hours}:${minutes}:${seconds}` : `${Number(minutes)}:${seconds}`;
};

export type TimerReading = {
  /** What the number shows: Countdown or Stopwatch. A session already used has nothing to count down. */
  mode: TimerMode;
  /** The number: time left, time used, or (counting down past the plan) time over it. */
  seconds: number;
  /** The planned time reached: "1h done", from 0:00 on. */
  done: boolean;
  /** Past the planned time by a minute or more; the first minute reads as the plan done, not as time over. */
  over: boolean;
  /** Of the plan, used so far (0–1): the ring's aqua. */
  usedFraction: number;
  /** Past the plan, as laps of it (0.5 = half a plan over, 1.7 = a lap and seven tenths): the ring goes round again. */
  overFraction: number;
};

/**
 * How a session's timer reads. The countdown counts down the planned amount (planned − used), not to the block's end,
 * so starting early or late still gives the whole time. One block, one used time (Petr, 2026-10-16): a session already
 * logged reads like any other, its plan being its logged time; past the plan, the ring goes round again.
 */
/**
 * Time over isn't shown for the first minute past the plan (Petr, 2026-10-31): "+0:16 over" in tangerine the moment
 * the plan was done read as a mistake. For that minute the countdown holds at 0:00, "1h done", while the pill asks.
 */
export const OVER_GRACE_SECONDS = 60;

export const readTimer = ({
  usedSeconds,
  plannedMinutes,
  mode
}: {
  usedSeconds: number;
  plannedMinutes?: number;
  mode: TimerMode;
}): TimerReading => {
  const used = Math.max(0, Math.floor(usedSeconds));
  const plan = Math.max(0, (plannedMinutes || 0) * 60);
  const clamp = (value: number) => Math.min(1, Math.max(0, value));

  if (!plan) return { mode: 'stopwatch', seconds: used, done: false, over: false, usedFraction: 0, overFraction: 0 };

  const done = used >= plan;
  const over = used - plan >= OVER_GRACE_SECONDS;
  return {
    mode,
    seconds: mode === 'stopwatch' ? used : done && !over ? 0 : Math.abs(plan - used),
    done,
    over,
    usedFraction: clamp(used / plan),
    overFraction: over ? (used - plan) / plan : 0
  };
};

/**
 * Runaway timers (timer spec, 2026-10-16): the pill checks in at the plan's end (0) and at each full lap past it (1, 2…);
 * -1 before the plan's end.
 */
export const checkInAt = (usedSeconds: number, planSeconds: number) =>
  planSeconds > 0 && usedSeconds >= planSeconds ? Math.floor(usedSeconds / planSeconds) - 1 : -1;

/** Two check-ins unanswered after the last one answered: the time the timer pauses at (when the second one came). */
export const pauseAfterUnanswered = (answeredCheckIn: number, planSeconds: number) =>
  planSeconds * (Math.max(0, answeredCheckIn) + 3);

/** Minutes as a student types them: "40", "1h 10m", "1h10", "1:10"; NaN when it isn't a time. */
export const parseMinutes = (text: string) => {
  const typed = text.trim().toLowerCase();
  const clock = typed.match(/^(\d+):(\d{1,2})$/);
  if (clock) return Number(clock[1]) * 60 + Number(clock[2]);
  const words = typed.match(/^(?:(\d+)\s*h)?\s*(?:(\d+)\s*m?)?$/);
  if (words && (words[1] || words[2])) return Number(words[1] || 0) * 60 + Number(words[2] || 0);
  return NaN;
};

/** The lap whose "Still studying?" is being asked: for the minute after it ends, until answered; -1 when none. */
export const lapAsking = (usedSeconds: number, planSeconds: number, answeredCheckIn: number) => {
  const checkIn = checkInAt(usedSeconds, planSeconds);
  if (checkIn < 1 || answeredCheckIn >= checkIn) return -1;
  return usedSeconds - (checkIn + 1) * planSeconds < 60 ? checkIn : -1;
};

export type TimerLapKind = 'plan' | 'keptGoing' | 'noAnswer' | 'now' | 'lap' | 'sincePaused';

export type TimerLap = {
  /** 0 is the plan, 1 the first lap past it. */
  index: number;
  kind: TimerLapKind;
  /** Its length; so far, for the lap under way. */
  seconds: number;
  /** Ticked to begin with: all of it while the student is there, and only what they said they studied once it paused itself. */
  ticked: boolean;
};

/**
 * A timer past its plan, lap by lap (timer spec, "Past the plan", Petr 2026-10-29: "more like laps on a stopwatch"): a
 * row per plan's length, the plan first. "Keep going" at a lap's end says it was studied. Running, or paused by the
 * student, every lap is ticked, as they are there to log it. Paused itself after two check-ins unanswered, only the plan
 * and the laps kept going through are, and the time since it paused is offered, unticked, in case they studied on.
 */
export const timerLaps = ({
  usedSeconds,
  planSeconds,
  running,
  kept,
  pausedItself,
  sincePausedSeconds = 0
}: {
  usedSeconds: number;
  planSeconds: number;
  running: boolean;
  kept: number[];
  pausedItself: boolean;
  sincePausedSeconds?: number;
}): TimerLap[] => {
  const used = Math.max(0, Math.floor(usedSeconds));
  if (planSeconds <= 0 || used <= planSeconds) return [];
  let count = Math.ceil(used / planSeconds);
  // A stopped timer's last few seconds go with the lap before: "Lap 4 · 0m" said nothing.
  if (!running && used - (count - 1) * planSeconds < 60) count -= 1;
  if (count < 2) return [];
  const laps: TimerLap[] = [];
  for (let index = 0; index < count; index++) {
    const last = index === count - 1;
    const keptGoing = kept.includes(index);
    laps.push({
      index,
      kind: index === 0 ? 'plan' : running && last ? 'now' : keptGoing ? 'keptGoing' : pausedItself ? 'noAnswer' : 'lap',
      seconds: last ? used - index * planSeconds : planSeconds,
      ticked: !pausedItself || index === 0 || keptGoing
    });
  }
  if (pausedItself && sincePausedSeconds >= 60) {
    laps.push({ index: count, kind: 'sincePaused', seconds: Math.floor(sincePausedSeconds), ticked: false });
  }
  return laps;
};

/** A session's planned time as the timer names it: 45m, 1h 00m. */
export const planLabel = (minutes: number) => (minutes < 60 ? `${minutes}m` : minutesToDurationInHours(minutes, true));

/** An amount to extend by: 15m, 30m, 1h, 1h 30m (whole hours without the 00m). */
export const extendLabel = (minutes: number) =>
  minutes % 60 === 0 && minutes > 0 ? `${minutes / 60}h` : planLabel(minutes);

/**
 * The tab's title while a timer runs and Shovel isn't the tab in view (timer spec step 6): the time first, so it
 * survives the browser cutting titles, then the session. In whole minutes (Petr, 2026-10-29): a hidden tab's clock
 * moves about once a minute, and the pill has the seconds whenever Shovel is in view. Time left and time over round
 * up, so the last seconds read "1m left" and the first ones past the plan "+1m over"; time used rounds down.
 */
export const timerTabTitle = ({ reading, paused, name }: { reading: TimerReading; paused: boolean; name?: string }) => {
  const countingDown = reading.mode === 'countdown';
  const minutes = extendLabel(countingDown ? Math.ceil(reading.seconds / 60) : Math.floor(reading.seconds / 60));
  const time = paused
    ? t.TIMER_TAB_PAUSED
    : !countingDown
    ? minutes
    : reading.over
    ? `+${minutes} ${t.TIMER_OVER}`
    : reading.done
    ? t.TIMER_TAB_TIMES_UP
    : `${minutes} ${t.TIMER_LEFT}`;
  return t.TIMER_TAB_TITLE(time, name);
};

/** A stretch of timed time in a message: 40 sec, 18 min, 1h 05m. */
export const spanLabel = (timeInSeconds: number) => {
  const seconds = Math.max(0, Math.round(timeInSeconds));
  const minutes = Math.floor(seconds / 60);
  if (minutes === 0) return `${seconds} sec`;
  return minutes < 60 ? `${minutes} min` : minutesToDurationInHours(minutes, true);
};

/**
 * How wide (in `ch`, the width of a 0) a ticking clock should keep its room: its longest form now (m:ss may grow to
 * 59:59; h:mm:ss with its hours), plus the "+" past the plan. The app's font has digits of different widths, so a clock
 * sized to its text grew and shrank every second (Petr, 2026-10-15). 0 is its widest digit; a colon is under half one
 * and "+" just under one (measured), so the room is no wider than the clock (Petr, 2026-10-17: the gaps were too wide).
 */
export const clockWidthCh = (timeInSeconds: number, over = false) => {
  const hours = Math.floor(Math.max(0, timeInSeconds) / 3600);
  const digits = hours ? String(hours).length + 4 : 4;
  const colons = hours ? 2 : 1;
  return digits + colons * 0.45 + (over ? 0.95 : 0);
};
